import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import hash from 'object-hash'

import { mergeDeepAll } from '../utils'
import { CompassAPIDataBase, getDataSources } from '../utils/compass'
import { chartPalette } from '../utils/constants'
import type { BaseContext } from './'
import { commonChartOptions } from './common'

export const FilterFields = [
  'empresa_nome',
  'aeroporto_de_origem_nome',
  'aeroporto_de_origem_uf',
  'aeroporto_de_origem_regiao',
  'aeroporto_de_origem_pais',
  'aeroporto_de_origem_continente',
  'aeroporto_de_destino_nome',
  'aeroporto_de_destino_uf',
  'aeroporto_de_destino_regiao',
  'aeroporto_de_destino_pais',
  'aeroporto_de_destino_continente',
  'natureza',
  'grupo_de_voo',
] as const

export type AnacFilters = Record<typeof FilterFields[number], string[]>

export namespace Airlines {
  export namespace Anac {
    const CONTEXT_ID = 'equity:airlines:anac'
    const AIRLINES_CHARTS_PATH = '/api/charts/airlines'
    const CHART_IDS = [
      'total_rpk_ask',
      'total_load_factor',
      'rpk_share',
      'ask_share',
    ] as const
    const CHART_ROUTES: Record<typeof CHART_IDS[number], string> = {
      total_rpk_ask: 'total-rpk-ask-yoy',
      total_load_factor: 'total-load-factor',
      rpk_share: 'rpk-share',
      ask_share: 'ask-share',
    }

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      chart: typeof CHART_IDS[number]
      context: typeof CONTEXT_ID
      chartFilter: AnacFilters
      title?: string
    }

    export interface ChartData extends CompassAPIDataBase {
      data: {
        name: string
        series_id: string
        data: {
          date: string
          value: number
        }[]
      }[]
      title: string
    }

    const getChartData = async (
      config: ChartConfig,
      modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
        conf
    ) => {
      const response = (await axios(
        modifier({
          url: AIRLINES_CHARTS_PATH + '/' + CHART_ROUTES[config.chart],
          method: 'POST',
          data: config.chartFilter,
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        color: chartPalette[idx],
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          value * 100,
        ]),
        id: s.name,
        marker: { enabled: false },
        name: s.name,
        tooltip: {
          xDateFormat: '%m/%Y',
          valueDecimals: 2,
        },
        type: config.chart === 'total_rpk_ask' ? 'spline' : 'column',
        yAxis: config.chart === 'total_rpk_ask' ? 'variation' : 'share',
      }))

      const yAxis = [
        {
          id: 'variation',
          opposite: false,
          title: { text: '' },
          labels: { format: '{value}%' },
        },
        {
          id: 'share',
          opposite: false,
          title: { text: '' },
          labels: { format: '{value}%' },
          max: 100,
        },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          {
            series,
            yAxis,
            legend: { enabled: config.chart !== 'total_load_factor' },
            plotOptions: { column: { stacking: 'normal' } },
            tooltip: { shared: false, split: true },
          },
        ]),
        stockTools: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
